import { datadogRum } from '@datadog/browser-rum';
import { InternalAxiosRequestConfig } from 'axios';
import React, { createContext, useEffect, useLayoutEffect, useReducer } from 'react';
import { useInterval } from 'usehooks-ts';
import Loader from '~/components/Loader';
import { BASE_PATH } from '~/config';
import { LOGIN, LOGOUT } from '~/contexts/auth-reducer/actions';
import authReducer from '~/contexts/auth-reducer/auth';
import { setDatadogUser } from '~/datadog';
import { redirectToCognitoLogout } from '~/pages/auth/redirectToCognito';
import { AuthProps, JWTContextType, Permission, RegisterProps, UserProfile } from '~/types/auth';
import axios from '~/utils/axios';
import { tokenManager } from '~/utils/graphql-fetcher';

/**
 * Module-scoped promise that deduplicates concurrent refresh-token calls.
 * When multiple 401 responses arrive simultaneously, only the first triggers
 * a POST /auth/refresh-token — all others await the same in-flight promise.
 */
let pendingRefresh: Promise<string> | null = null;
let tokenVersion = 0;
let latestToken = '';

/**
 * Refreshes the access token using the refresh token.
 * Concurrent callers share a single in-flight request to avoid token races.
 * @returns A promise that resolves to the new access token.
 */
function refreshAccessToken(): Promise<string> {
  if (!pendingRefresh) {
    pendingRefresh = axios
      .post('/auth/refresh-token')
      .then((res) => {
        tokenVersion++;
        latestToken = res.data.token;
        return latestToken;
      })
      .finally(() => {
        pendingRefresh = null;
      });
  }
  return pendingRefresh;
}

const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  userId: null,
  token: null,
  requires2FA: false,
  isBot: false,
  user: null,
  userRole: 'enduser',
  permissions: [],
};

const fetchAndUpdateUserDetails = async (
  dispatch: React.Dispatch<any>,
  token?: string | null,
): Promise<void> => {
  try {
    // If no token is provided, try to refresh token from cookie
    // This allows initial page load to authenticate if refresh token exists
    if (!token) {
      try {
        token = await refreshAccessToken();
      } catch (refreshError) {
        datadogRum.addAction('auth_no_session', { feature: 'auth' });
        dispatch({ type: 'INITIALIZED' });
        return;
      }
    }

    const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    const response = await axios.get('/me/profile/', config);
    const user: UserProfile = response.data.user.user;
    const verificationData = response.data.user.verificationData;
    const userRole = response.data.user.user.role;
    const permissions = response.data.user.user.permissions || [];

    // Check for force logout flag
    if (user.forceLogout === true) {
      datadogRum.addError(new Error('Force logout detected'), {
        feature: 'auth',
        errorType: 'force_logout',
      });
      dispatch({ type: LOGOUT });
      return;
    }

    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user,
        userRole,
        verificationData,
        permissions,
        requires2FA: false,
        token: response.data.token || token,
      },
    });

    dispatch({ type: 'INITIALIZED' });
  } catch (error) {
    datadogRum.addError(error, {
      feature: 'auth',
      errorType: 'profile_fetch_error',
      context: 'fetch_user_details',
    });

    // if this fails the axios refresh token interceptor will try again
    // if that fails the user is logged out
  }
};

const JWTContext = createContext<JWTContextType | null>(null);

export const JWTProvider: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    // Only try to fetch user details on mount if we haven't been initialized yet
    // Once initialized, we only fetch if explicitly logged in (via polling or login)
    // This prevents re-authentication after logout
    if (state.isInitialized) {
      return;
    }
    fetchAndUpdateUserDetails(dispatch, undefined);
  }, [state.isInitialized]);

  // Set up Datadog user context
  useEffect(() => {
    if (state.isLoggedIn && state.user) {
      setDatadogUser({
        id: state.user.userId?.toString?.() || '',
        email: state.user.email || '',
      });
    }
  }, [state.isLoggedIn, state.user, state.userId]);

  useEffect(() => {
    if (state.isLoggedIn) {
      datadogRum.addAction('auth_session_started', { feature: 'auth' });
    }
  }, [state.isLoggedIn]);

  useInterval(() => {
    if (!state.isLoggedIn || !state.token) {
      return;
    }
    if (pendingRefresh) return;

    if (
      ['/login', '/register', '/verify-user', '/set-password', '/forgot-password'].includes(
        window.location.pathname,
      )
    ) {
      return;
    }

    fetchAndUpdateUserDetails(dispatch, state.token);
  }, 60_000);

  useLayoutEffect(() => {
    const authInterceptor = axios.interceptors.request.use(
      async (config: InternalAxiosRequestConfig & { _retry?: boolean; _tokenVersion?: number }) => {
        config._tokenVersion = tokenVersion;
        config.headers.Authorization =
          !config._retry && state.token ? `Bearer ${state.token}` : config.headers.Authorization;
        return config;
      },
    );

    return () => {
      axios.interceptors.request.eject(authInterceptor);
    };
  }, [state.token]);

  useLayoutEffect(() => {
    const refreshTokenInterceptor = axios.interceptors.response.use(
      async (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (!error.response) {
          datadogRum.addError(error, {
            feature: 'auth',
            errorType: 'network_error',
            context: 'response_interceptor',
            originalUrl: originalRequest?.url,
          });
          return Promise.reject(error);
        }

        if (
          error.response.status === 401 &&
          error.response.data.message === 'Session has been terminated'
        ) {
          datadogRum.addError(error, {
            feature: 'auth',
            errorType: 'session_terminated',
            context: 'response_interceptor',
            originalUrl: originalRequest?.url,
          });
          delete axios.defaults.headers.common.Authorization;
          tokenManager.clear();
          dispatch({ type: LOGOUT });
          window.location.href = `${BASE_PATH}/login`;
          return Promise.reject(error);
        }

        if (error.response.status === 401 && error.response.data.message === 'Unauthorized') {
          if (originalRequest.url?.includes('/auth/logout')) {
            return Promise.reject(error);
          }
          if (originalRequest._retry) {
            return Promise.reject(error);
          }
          originalRequest._retry = true;

          if (
            originalRequest._tokenVersion !== undefined &&
            originalRequest._tokenVersion < tokenVersion
          ) {
            originalRequest.headers.Authorization = `Bearer ${latestToken}`;
            return axios(originalRequest);
          }

          try {
            const token = await refreshAccessToken();
            dispatch({ type: LOGIN, payload: { token } });
            datadogRum.addAction('auth_token_refreshed', {
              feature: 'auth',
              originalUrl: originalRequest.url,
            });
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axios(originalRequest);
          } catch (refreshError) {
            datadogRum.addError(refreshError, {
              feature: 'auth',
              errorType: 'auth_refresh_error',
              context: 'response_interceptor',
              originalUrl: originalRequest.url,
            });
            delete axios.defaults.headers.common.Authorization;
            tokenManager.clear();
            dispatch({ type: LOGOUT });
            window.location.href = `${BASE_PATH}/login`;
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.response.eject(refreshTokenInterceptor);
    };
  }, []);

  const verifyUser = async (
    email: string,
    password: string,
  ): Promise<{
    userId: number;
    cognitoSub: string | null;
  }> => {
    try {
      const response = await axios.post('/auth/verify-user', { email, password });

      const { userId, cognitoSub } = response.data;

      datadogRum.addAction('auth_verify_user_success', {
        feature: 'auth',
        userId: userId?.toString(),
      });

      return { userId, cognitoSub };
    } catch (error) {
      datadogRum.addError(error, {
        feature: 'auth',
        errorType: 'verify_user_error',
        context: 'verify_user',
      });
      throw error;
    }
  };

  const register = async (registerProps: RegisterProps): Promise<void> => {
    await axios.post('/auth/register', {
      registerProps,
    });
  };

  // @todo -- does this work with cognito? we probably have to do something else
  const registerAdmin = async (registerProps: RegisterProps): Promise<void> => {
    await axios.post('/auth/register-admin', {
      registerProps,
    });
  };

  const resetPassword = async (email: string): Promise<void> => {
    // Implement password reset logic here
    await axios.post('/auth/reset-password', { email });
  };

  const logout = async (): Promise<void> => {
    delete axios.defaults.headers.common.Authorization;
    tokenManager.clear();
    dispatch({ type: LOGOUT });

    try {
      datadogRum.addAction('auth_logout', { feature: 'auth' });
      await axios.post('/auth/logout');
    } catch (error) {
      datadogRum.addError(error, {
        feature: 'auth',
        errorType: 'logout_error',
        context: 'logout',
      });
    }

    await redirectToCognitoLogout();
  };

  const updateProfile = (): void => {
    fetchAndUpdateUserDetails(dispatch, state.token);
  };

  if (!state.isInitialized) {
    return <Loader />;
  }

  const hasPermission = (requiredPermission: Permission | Permission[]): boolean => {
    if (state.userRole === 'superadmin') {
      //We want to check if permission is enduser, if it is, we want to return false. also account for array of permissions
      if (Array.isArray(requiredPermission) && requiredPermission.includes('enduser')) {
        return false;
      }

      if (requiredPermission === 'enduser') {
        return false;
      }
      return true;
    }

    if (state.userRole === 'enduser') {
      if (Array.isArray(requiredPermission)) {
        return requiredPermission.some((policy) => state.permissions.includes(policy));
      }
      return state.permissions.includes(requiredPermission);
    }

    // For admin roles, check against their specific permissions
    if (Array.isArray(requiredPermission)) {
      return requiredPermission.some((policy) => state.permissions.includes(policy));
    }
    return state.permissions.includes(requiredPermission);
  };

  const verifyLegacyEmail = async (
    email: string,
  ): Promise<{ legacyEmail?: boolean; message?: string | null }> => {
    const response = await axios.post('/auth/verifyLegacyEmail', { email });
    const { legacyEmail, message } = response.data;
    return { legacyEmail, message };
  };

  const checkCognitoSub = async (
    email: string,
  ): Promise<{ hasCognitoSub: boolean; cognitoSub?: string | null }> => {
    const response = await axios.post('/auth/check-cognito-sub', { email });
    const { hasCognitoSub, cognitoSub } = response.data;
    return { hasCognitoSub, cognitoSub };
  };

  const resendVerification = async (
    email: string,
    recaptchaToken: string,
  ): Promise<{ success: boolean; message?: string }> => {
    const response = await axios.post('/auth/resend-verification', { email, recaptchaToken });
    return response.data;
  };

  const checkVerificationStatus = async (email: string): Promise<{ verified: boolean }> => {
    const response = await axios.post('/auth/check-verification-status', { email });
    return response.data;
  };

  // @todo fixup when cognito is fully implemented
  // @ts-ignore
  const contextValue: JWTContextType = {
    ...state,
    verifyUser,
    logout,
    register,
    registerAdmin,
    resetPassword,
    updateProfile,
    verifyLegacyEmail,
    checkCognitoSub,
    resendVerification,
    checkVerificationStatus,
    hasPermission,
  };

  return <JWTContext.Provider value={contextValue}>{children}</JWTContext.Provider>;
};

export default JWTContext;
